import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useI18n } from '../contexts/I18nContext';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
  position: relative;
  background: linear-gradient(135deg, #F5F5DC 0%, #E8D5B7 100%);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(0,0,0,0.05)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
    z-index: 0;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    min-height: 90vh;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.5rem;
    min-height: 85vh;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  width: 100%;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const Title = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #2C3E50;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  line-height: 1.1;
  animation: fadeInUp 0.8s ease-out;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  color: #34495E;
  margin-bottom: 2rem;
  line-height: 1.6;
  animation: fadeInUp 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 1.2rem;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 3rem;
  margin-bottom: 3rem;
  justify-content: center;
  flex-wrap: wrap;
  animation: fadeInUp 0.8s ease-out 0.4s both;

  @media (max-width: 768px) {
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
    margin-bottom: 1.5rem;
    flex-direction: column;
    align-items: center;
  }
`;

const StatItem = styled.div`
  text-align: center;
  min-width: 80px;

  @media (max-width: 480px) {
    min-width: 60px;
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #8B4513; /* Deep sage color */
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #5D6D7E;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  animation: fadeInUp 0.8s ease-out 0.6s both;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0 1rem;
  }
`;

const PrimaryButton = styled(Link)`
  font-family: 'Inter', sans-serif;
  padding: var(--space-4) var(--space-10);
  background: linear-gradient(135deg, var(--warm-600) 0%, var(--warm-700) 100%);
  color: white;
  border: none;
  border-radius: var(--radius-full);
  font-size: var(--font-size-lg);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  box-shadow: var(--shadow-lg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  min-width: 180px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-xl);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  &:focus {
    outline: 2px solid var(--accent-400);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-8);
    font-size: var(--font-size-base);
    min-width: 160px;
  }

  @media (max-width: 480px) {
    padding: var(--space-4);
    font-size: var(--font-size-sm);
    min-width: 100%;
    min-height: 48px;
    width: 100%;
    max-width: 280px;
  }
`;

const SecondaryButton = styled(Link)`
  font-family: 'Inter', sans-serif;
  padding: var(--space-4) var(--space-10);
  background: transparent;
  color: var(--warm-700);
  border: 2px solid var(--warm-700);
  border-radius: var(--radius-full);
  font-size: var(--font-size-lg);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  min-width: 180px;
  text-align: center;
  position: relative;

  &:hover {
    background: var(--warm-700);
    color: white;
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:active {
    transform: translateY(-1px);
  }

  &:focus {
    outline: 2px solid var(--accent-400);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-8);
    font-size: var(--font-size-base);
    min-width: 160px;
  }

  @media (max-width: 480px) {
    padding: var(--space-4);
    font-size: var(--font-size-sm);
    min-width: 100%;
    min-height: 48px;
    width: 100%;
    max-width: 280px;
  }
`;

// Video Section Styles
const VideoSection = styled.section`
  background: linear-gradient(135deg, #F5F5DC 0%, #E8D5B7 100%);
  padding: 5rem 2rem;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(139, 69, 19, 0.03)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
    z-index: 0;
  }
`;

const VideoContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const VideoContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: fadeInUp 0.8s ease-out;
`;

const VideoTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #8B4513;
  margin-bottom: 1.5rem;
  text-shadow: 1px 1px 2px rgba(139, 69, 19, 0.1);

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const VideoDescription = styled.p`
  font-size: 1.25rem;
  color: #34495E;
  line-height: 1.6;
  margin-bottom: 3rem;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  aspect-ratio: 16/9;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(139, 69, 19, 0.3);
  border: 4px solid rgba(139, 69, 19, 0.2);
  background: #000;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(139, 69, 19, 0.05), rgba(160, 82, 45, 0.05));
    border-radius: 12px;
    pointer-events: none;
    z-index: 1;
  }

  iframe {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
    border: none;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0 1rem;
    aspect-ratio: 16/10; /* Slightly taller on tablets */
    border-radius: 12px;
    border-width: 3px;
  }

  @media (max-width: 480px) {
    margin: 0 0.5rem;
    aspect-ratio: 4/3; /* Even taller on mobile */
    border-radius: 8px;
    border-width: 2px;
  }

  @media (max-width: 380px) {
    margin: 0;
    aspect-ratio: 3/2; /* Maximum height on very small screens */
    border-radius: 0;
    border-width: 1px;
  }
`;

const GameSection = styled.section`
  background: linear-gradient(135deg, #F5F5DC 0%, #E8D5B7 100%);
  padding: 5rem 2rem;
  position: relative;
`;

const GameContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  text-align: center;
`;

const GameContent = styled.div`
  animation: fadeInUp 0.8s ease-out;
  width: 100%;
`;

const GameTitle = styled.h2`
  font-size: 2.5rem;
  color: #2C3E50;
  margin-bottom: 1.5rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const GameDescription = styled.p`
  font-size: 1.1rem;
  color: #34495E;
  line-height: 1.8;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const GameFeatures = styled.div`
  margin-bottom: 3rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  color: #8B4513;
  margin-bottom: 1.5rem;
  font-weight: 700;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #8B4513, #A0522D);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
`;

const FeatureText = styled.div`
  flex: 1;
`;

const FeatureName = styled.div`
  font-weight: 600;
  color: #2C3E50;
  margin-bottom: 0.25rem;
`;

const FeatureDesc = styled.div`
  font-size: 0.9rem;
  color: #5D6D7E;
`;

const DownloadButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: #000;
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &.google-play {
    background: #4285F4;
  }

  &.app-store {
    background: #007AFF;
  }
`;

const ExploreButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 3rem;
  background: linear-gradient(135deg, #8B4513, #A0522D);
  color: white;
  border: none;
  border-radius: 50px;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(139, 69, 19, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(139, 69, 19, 0.4);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 1.1rem;
  }
`;


const Home: React.FC = () => {
  const { t } = useI18n();

  return (
    <>
      <HeroSection>
        <HeroContent>
          <Title>{t('home.title')}</Title>
          <Subtitle>
            {t('home.subtitle')}
          </Subtitle>

          <StatsContainer>
            <StatItem>
              <StatNumber>9</StatNumber>
              <StatLabel>{t('home.chapters')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>31+</StatNumber>
              <StatLabel>{t('home.levels')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>100%</StatNumber>
              <StatLabel>{t('home.solutions')}</StatLabel>
            </StatItem>
          </StatsContainer>

          <ButtonContainer>
            <PrimaryButton to="/chapters">{t('home.exploreChapters')}</PrimaryButton>
            <SecondaryButton to="/about">{t('home.aboutGuide')}</SecondaryButton>
          </ButtonContainer>
        </HeroContent>
      </HeroSection>

      <VideoSection>
        <VideoContainer>
          <VideoContent>
            <VideoTitle>{t('home.videoTitle')}</VideoTitle>
            <VideoDescription>
              {t('home.videoDescription')}
            </VideoDescription>
            <VideoWrapper>
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/KWAMNaIgpJU"
                title="Monument Valley 3 Complete Walkthrough"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ borderRadius: '12px' }}
              />
            </VideoWrapper>
          </VideoContent>
        </VideoContainer>
      </VideoSection>

      <GameSection>
        <GameContainer>
          <GameContent>
            <GameTitle>{t('home.gameTitle')}</GameTitle>
            <GameDescription>
              {t('home.gameDescription')}
            </GameDescription>

            <GameFeatures>
              <FeatureTitle>{t('home.features.title')}</FeatureTitle>
              <FeatureGrid>
                <FeatureItem>
                  <FeatureIcon>⊚</FeatureIcon>
                  <FeatureText>
                    <FeatureName>{t('home.features.opticalIllusions.name')}</FeatureName>
                    <FeatureDesc>{t('home.features.opticalIllusions.desc')}</FeatureDesc>
                  </FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>◈</FeatureIcon>
                  <FeatureText>
                    <FeatureName>{t('home.features.impossibleArchitecture.name')}</FeatureName>
                    <FeatureDesc>{t('home.features.impossibleArchitecture.desc')}</FeatureDesc>
                  </FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>◆</FeatureIcon>
                  <FeatureText>
                    <FeatureName>{t('home.features.beautifulArt.name')}</FeatureName>
                    <FeatureDesc>{t('home.features.beautifulArt.desc')}</FeatureDesc>
                  </FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>⬟</FeatureIcon>
                  <FeatureText>
                    <FeatureName>{t('home.features.intuitiveControls.name')}</FeatureName>
                    <FeatureDesc>{t('home.features.intuitiveControls.desc')}</FeatureDesc>
                  </FeatureText>
                </FeatureItem>
              </FeatureGrid>
            </GameFeatures>

            <DownloadButtons>
              <DownloadButton
                href="https://apps.apple.com/us/app/monument-valley-3/id6443563379"
                target="_blank"
                rel="noopener noreferrer"
                className="app-store"
              >
                <span style={{ marginRight: '8px' }}>⬤</span> {t('game.downloadFromAppStore')}
              </DownloadButton>
              <DownloadButton
                href="https://play.google.com/store/apps/details?id=com.ustwogames.mv3&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="google-play"
              >
                <span style={{ marginRight: '8px' }}>▲</span> {t('game.downloadFromGooglePlay')}
              </DownloadButton>
            </DownloadButtons>
          </GameContent>

          <ExploreButton to="/chapters">
            {t('home.exploreAllChapters')}
          </ExploreButton>
        </GameContainer>
      </GameSection>
    </>
  );
};

export default Home;